import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  DesignTestInputDto,
  DesignTestOutputDto
} from '../../../models/job-request-iterations-dto.interface';

interface HphtRheologyReportRow {
  temperature: number;
  pressure?: number;
  rpm600?: number;
  rpm300?: number;
  rpm200?: number;
  rpm100?: number;
  rpm60?: number;
  rpm30?: number;
  rpm20?: number;
  rpm10?: number;
  rpm6?: number;
  rpm3?: number;
  plasticViscosity?: number;
  yieldPoint?: number;
  gel10Sec?: number;
  gel10Min?: number;
  isReference?: boolean;
}

interface XmlTemperatureValues {
  id: string | null;
  temperature: number;
  values: Map<string, string>;
}

@Component({
  selector: 'app-hpht-rheology-test-report',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './hpht-rheology-test-report.component.html',
  styleUrl: './hpht-rheology-test-report.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class HphtRheologyTestReportComponent {
  private static readonly FANN35_REFERENCE_TEMPERATURE = 80;

  @Input({ required: true }) test!: DesignTestInputDto;
  @Input({ required: true }) output!: DesignTestOutputDto;
  @Input() referenceTest: DesignTestInputDto | null = null;

  protected hphtRheologyReportRows(): HphtRheologyReportRow[] | null {
    const referenceRows = this.referenceTest
      ? this.referenceReportRows(this.referenceTest)
      : [];
    const resultRows = this.hphtReportRows();
    const rows = [
      ...referenceRows.map(row => ({ ...row, isReference: true })),
      ...resultRows
    ];

    return rows.length ? rows : null;
  }

  protected hasReferenceRows(rows: HphtRheologyReportRow[]): boolean {
    return rows.some(row => row.isReference);
  }

  protected reportTitle(): string {
    return this.test.title || 'FANN77 HTHP Rheology';
  }

  protected formatTemperature(row: HphtRheologyReportRow): string {
    return `${this.formatValue(row.temperature)}${row.isReference ? '*' : ''}`;
  }

  protected formatValue(value: number | undefined, showTttm = false): string {
    if (value === undefined) return '—';
    if (showTttm && value >= 350) return 'TTTM';
    return value.toFixed(0);
  }

  private hphtReportRows(): HphtRheologyReportRow[] {
    const inputXml = this.xmlValuesByTemperature(this.test.content, 'input');
    const outputXml = this.xmlValuesByTemperature(this.output.content, 'output', this.test.content);
    if (inputXml.length > 0) {
      return inputXml.map((input, index) => {
        const output = input.id
          ? outputXml.find(reading => reading.id === input.id)
            ?? (outputXml[index]?.id ? undefined : outputXml[index])
          : outputXml[index];
        const hasUniqueTemperature = inputXml.filter(reading => reading.temperature === input.temperature).length === 1;
        return this.createRow(
          input.temperature,
          suffix => this.toNumber(output?.values.get(suffix))
            ?? this.toNumber(input.values.get(suffix))
            ?? this.parsedNumber(`reading:${index + 1}:${suffix}`, [this.output, this.test])
            ?? (hasUniqueTemperature
              ? this.keyedNumber(input.temperature, suffix, [this.output, this.test])
              : undefined),
          false
        );
      });
    }

    const temperatures = new Set<number>([
      ...this.keyedTemperatures(this.test),
      ...this.keyedTemperatures(this.output),
      ...inputXml.map(row => row.temperature),
      ...outputXml.map(row => row.temperature)
    ]);

    return Array.from(temperatures)
      .sort((first, second) => first - second)
      .map(temperature => this.createRow(
        temperature,
        (suffix: string) => this.hphtValue(temperature, suffix, inputXml, outputXml),
        false
      ))
      .filter(row => this.hasValues(row));
  }

  private referenceReportRows(referenceTest: DesignTestInputDto): HphtRheologyReportRow[] {
    const referenceOutput = referenceTest.testOutputs[0] ?? this.emptyOutput();
    const inputXml = this.xmlValuesByTemperature(referenceTest.content, 'input');
    const outputXml = this.xmlValuesByTemperature(referenceOutput.content, 'output', referenceTest.content);
    const temperatures = new Set<number>([
      ...this.keyedTemperatures(referenceTest),
      ...this.keyedTemperatures(referenceOutput),
      ...inputXml.map(row => row.temperature),
      ...outputXml.map(row => row.temperature)
    ]);

    const sortedTemperatures = Array.from(temperatures).sort((first, second) => first - second);
    const preferredTemperature = sortedTemperatures.includes(HphtRheologyTestReportComponent.FANN35_REFERENCE_TEMPERATURE)
      ? HphtRheologyTestReportComponent.FANN35_REFERENCE_TEMPERATURE
      : sortedTemperatures[0];
    if (preferredTemperature === undefined) return [];

    const preferredRow = this.createRow(
      preferredTemperature,
      (suffix: string) => this.referenceValue(preferredTemperature, suffix, referenceTest, referenceOutput, inputXml, outputXml),
      true
    );
    if (this.hasValues(preferredRow)) return [preferredRow];

    const fallbackRow = sortedTemperatures
      .filter(temperature => temperature !== preferredTemperature)
      .map(temperature => this.createRow(
        temperature,
        (suffix: string) => this.referenceValue(temperature, suffix, referenceTest, referenceOutput, inputXml, outputXml),
        true
      ))
      .find(row => this.hasValues(row));

    return fallbackRow ? [fallbackRow] : [];
  }

  private createRow(
    temperature: number,
    valueFor: (suffix: string) => number | undefined,
    isReference: boolean
  ): HphtRheologyReportRow {
    const rpm300 = valueFor('rpm300');
    const rpm200 = valueFor('rpm200');
    const enteredRpm600 = valueFor('rpm600');
    const calculatedRpm600 = enteredRpm600 === undefined && rpm300 !== undefined && rpm200 !== undefined
      ? (rpm300 - rpm200) * 3 + rpm300
      : undefined;
    const rpm600 = enteredRpm600 ?? calculatedRpm600;
    const plasticViscosity = rpm600 !== undefined && rpm300 !== undefined
      ? rpm600 - rpm300
      : undefined;

    return {
      temperature,
      pressure: valueFor('pressure'),
      rpm600,
      rpm300,
      rpm200,
      rpm100: valueFor('rpm100'),
      rpm60: valueFor('rpm60'),
      rpm30: valueFor('rpm30'),
      rpm20: valueFor('rpm20'),
      rpm10: valueFor('rpm10'),
      rpm6: valueFor('rpm6'),
      rpm3: valueFor('rpm3'),
      plasticViscosity,
      yieldPoint: plasticViscosity !== undefined && rpm300 !== undefined
        ? rpm300 - plasticViscosity
        : undefined,
      gel10Sec: valueFor('gel10s'),
      gel10Min: valueFor('gel10m'),
      isReference
    };
  }

  private hasValues(row: HphtRheologyReportRow): boolean {
    return [
      row.pressure,
      row.rpm600,
      row.rpm300,
      row.rpm200,
      row.rpm100,
      row.rpm60,
      row.rpm30,
      row.rpm20,
      row.rpm10,
      row.rpm6,
      row.rpm3,
      row.gel10Sec,
      row.gel10Min
    ].some(value => value !== undefined);
  }

  private hphtValue(
    temperature: number,
    suffix: string,
    inputXml: XmlTemperatureValues[],
    outputXml: XmlTemperatureValues[]
  ): number | undefined {
    const keyedValue = this.keyedNumber(temperature, suffix, [this.output, this.test]);
    if (keyedValue !== undefined) return keyedValue;

    return this.xmlNumber(temperature, suffix, [outputXml, inputXml]);
  }

  private referenceValue(
    temperature: number,
    suffix: string,
    referenceTest: DesignTestInputDto,
    referenceOutput: DesignTestOutputDto,
    inputXml: XmlTemperatureValues[],
    outputXml: XmlTemperatureValues[]
  ): number | undefined {
    const keyedValue = this.keyedNumber(temperature, suffix, [referenceOutput, referenceTest]);
    if (keyedValue !== undefined) return keyedValue;

    const parsedValue = this.parsedNumber(suffix, [referenceOutput, referenceTest]);
    if (parsedValue !== undefined) return parsedValue;

    return this.xmlNumber(temperature, suffix, [outputXml, inputXml]);
  }

  private keyedTemperatures(source: DesignTestInputDto | DesignTestOutputDto): number[] {
    const fields = 'parsedValues' in source ? source.parsedValues : source.parsedInputValues;
    const temperatures = fields
      .map(field => this.keyedTemperature(field.label))
      .filter((temperature): temperature is number => temperature !== undefined);

    return Array.from(new Set(temperatures));
  }

  private keyedTemperature(label: string): number | undefined {
    const normalized = this.normalize(label);
    const match = normalized.match(/^t(-?\d+(?:\.\d+)?)(?=pressure|rpm|gel|end|f2spring)/);
    return match ? this.toNumber(match[1]) : undefined;
  }

  private keyedNumber(
    temperature: number,
    suffix: string,
    sources: Array<DesignTestInputDto | DesignTestOutputDto>
  ): number | undefined {
    const acceptedNames = new Set([
      this.normalize(`t${temperature}_${suffix}`),
      this.normalize(`t${temperature}${suffix}`)
    ]);

    for (const source of sources) {
      const fields = 'parsedValues' in source ? source.parsedValues : source.parsedInputValues;
      const field = fields.find(candidate => acceptedNames.has(this.normalize(candidate.label)));
      const value = this.toNumber(field?.value);
      if (value !== undefined) return value;
    }

    return undefined;
  }

  private parsedNumber(
    suffix: string,
    sources: Array<DesignTestInputDto | DesignTestOutputDto>
  ): number | undefined {
    const acceptedNames = new Set([
      this.normalize(suffix),
      ...this.xmlNamesForSuffix(suffix)
    ]);

    for (const source of sources) {
      const fields = 'parsedValues' in source ? source.parsedValues : source.parsedInputValues;
      const field = fields.find(candidate => acceptedNames.has(this.normalize(candidate.label)));
      const value = this.toNumber(field?.value);
      if (value !== undefined) return value;
    }

    return undefined;
  }

  private xmlNumber(
    temperature: number,
    suffix: string,
    sources: XmlTemperatureValues[][]
  ): number | undefined {
    for (const source of sources) {
      const row = source.find(candidate => candidate.temperature === temperature);
      const value = this.toNumber(row?.values.get(suffix));
      if (value !== undefined) return value;
    }

    return undefined;
  }

  private xmlValuesByTemperature(
    content: string | null,
    kind: 'input' | 'output',
    inputContent?: string | null
  ): XmlTemperatureValues[] {
    if (!content) return [];

    const document = new DOMParser().parseFromString(content, 'application/xml');
    if (document.querySelector('parsererror')) return [];

    const inputReadings = kind === 'output'
      ? this.readingElements(inputContent, 'input')
      : [];
    const inputById = new Map<string, number>();
    const inputTemperatures = inputReadings
      .map(reading => {
        const temperature = this.elementValue(reading, 'temperature');
        const id = this.elementValue(reading, 'id');
        const parsedTemperature = this.toNumber(temperature);
        if (id && parsedTemperature !== undefined) inputById.set(id, parsedTemperature);
        return parsedTemperature;
      })
      .filter((temperature): temperature is number => temperature !== undefined);

    return this.readingElementsFromDocument(document, kind)
      .map((reading, index) => {
        const id = this.elementValue(reading, 'id');
        const directTemperature = this.toNumber(this.elementValue(reading, 'temperature'));
        const temperature = directTemperature
          ?? (id ? inputById.get(id) : undefined)
          ?? inputTemperatures[index]
          ?? [80, 120, 150][index];
        if (temperature === undefined) return null;

        return {
          id,
          temperature,
          values: this.xmlReadingValues(reading, kind)
        };
      })
      .filter((row): row is XmlTemperatureValues => row !== null);
  }

  private readingElements(content: string | null | undefined, kind: 'input' | 'output'): Element[] {
    if (!content) return [];
    const document = new DOMParser().parseFromString(content, 'application/xml');
    if (document.querySelector('parsererror')) return [];
    return this.readingElementsFromDocument(document, kind);
  }

  private readingElementsFromDocument(document: Document, kind: 'input' | 'output'): Element[] {
    const acceptedNames = kind === 'input'
      ? new Set(['rheologyinputreading', 'rheologyinputreadinglegacy', 'reading'])
      : new Set(['rheologyoutputreading', 'rheologyoutputreadinglegacy', 'reading']);

    return Array.from(document.getElementsByTagName('*'))
      .filter(element => acceptedNames.has(element.localName.toLowerCase()));
  }

  private xmlReadingValues(element: Element, kind: 'input' | 'output'): Map<string, string> {
    const values = new Map<string, string>();
    for (const child of Array.from(element.children)) {
      const value = child.textContent?.trim();
      if (!value) continue;

      const normalized = this.normalize(child.localName);
      if (kind === 'input' && normalized === 'pressure') {
        values.set('pressure', value);
      }
      if (kind === 'output') {
        const suffix = this.suffixForXmlName(normalized);
        if (suffix) values.set(suffix, value);
      }
    }

    return values;
  }

  private suffixForXmlName(name: string): string | null {
    const names: Record<string, string> = {
      viscosity600rpm: 'rpm600',
      viscosity300rpm: 'rpm300',
      viscosity200rpm: 'rpm200',
      viscosity100rpm: 'rpm100',
      viscosity60rpm: 'rpm60',
      viscosity30rpm: 'rpm30',
      viscosity20rpm: 'rpm20',
      viscosity10rpm: 'rpm10',
      viscosity6rpm: 'rpm6',
      viscosity3rpm: 'rpm3',
      viscositygel10sec600rpm: 'gel10s600',
      viscositygel10sec300rpm: 'gel10s300',
      viscositygel10min600rpm: 'gel10m600',
      viscosityend600rpm: 'end600',
      gel10sec: 'gel10s',
      gel10min: 'gel10m',
      gel30min: 'gel30m'
    };

    return names[name] ?? null;
  }

  private xmlNamesForSuffix(suffix: string): string[] {
    const xmlName = Object.entries({
      viscosity600rpm: 'rpm600',
      viscosity300rpm: 'rpm300',
      viscosity200rpm: 'rpm200',
      viscosity100rpm: 'rpm100',
      viscosity60rpm: 'rpm60',
      viscosity30rpm: 'rpm30',
      viscosity20rpm: 'rpm20',
      viscosity10rpm: 'rpm10',
      viscosity6rpm: 'rpm6',
      viscosity3rpm: 'rpm3',
      gel10sec: 'gel10s',
      gel10min: 'gel10m'
    }).find(([, value]) => value === suffix)?.[0];

    return xmlName ? [xmlName] : [];
  }

  private elementValue(element: Element, name: string): string | null {
    const child = Array.from(element.children)
      .find(candidate => this.normalize(candidate.localName) === this.normalize(name));
    const value = child?.textContent?.trim();
    return value || null;
  }

  private emptyOutput(): DesignTestOutputDto {
    return {
      id: 0,
      content: null,
      isDefault: null,
      equipmentName: null,
      parsedValues: []
    };
  }

  private normalize(value: string | null | undefined): string {
    return (value ?? '').replace(/[\s_\-/,().:%]+/g, '').toLowerCase();
  }

  private toNumber(value: string | number | null | undefined): number | undefined {
    if (value === null || value === undefined || value === '') return undefined;
    const parsed = typeof value === 'number' ? value : Number(value.replace(/,/g, ''));
    return Number.isFinite(parsed) ? parsed : undefined;
  }
}
